import * as React from 'react';
import { capitalize } from 'lodash';
import {
  ROOT_PAGE,
  TOURNAMENT_MARKET_PLACE,
  getCityAllCourtVenueUrlWithSport,
  getCityCourtsPageUrlWithSport,
  getCountryIndexPageUrlWithSport,
  getCountrySubdivisionIndexPageUrlWithSport,
  getCourtPageUrlWithSport,
  getEventUrl,
} from 'constants/pages';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { getEventLocation } from 'utils/shared/location/getEventLocation';
import { useGeoLocation } from 'hooks/useGeoLocation';
import City from 'svg/City';
import Home from 'svg/Home';
import TournamentList from 'screens/TournamentMarketPlace/TournamentList';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Breadcrumbs from 'components/Breadcrumbs';
import Button, { ButtonLink } from 'components/Button';
import Footer from 'components/Footer';
import Link from 'components/Link';
import CardTournament from 'components/cards/CardTournament';
import CardVenue from 'components/cards/CardVenue';
import TopNav from 'components/nav/TopNav/TopNav';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import classNames from 'styles/utils/classNames';
import { Props } from './types';

const VISIBLE_CITIES = 16;
const VISIBLE_VENUES = 16;

type Venues = Pick<Props, 'venues'>;

export const generateBreadcrumbs = ({
  isForJsonLd,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  isForJsonLd?: boolean;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  return {
    ui: isForJsonLd
      ? []
      : [
          {
            label: <Home className="w-4" />,
            url: ROOT_PAGE,
          },
          {
            label: countryId || '',
            url: getCountryIndexPageUrlWithSport(countrySlug || '', sport),
          },
          {
            label: regionName || '',
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      {
        name: 'Home',
        id: ROOT_PAGE,
      },
      {
        name: `${countryName || ''} ${sportName}`,
        id: getCountryIndexPageUrlWithSport(countrySlug || '', sport),
      },
      {
        name: `${regionName || ''} ${sportName}`,
        id: getCountrySubdivisionIndexPageUrlWithSport(regionSlug || '', sport),
      },
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};

const CityLinkList = ({ cities, sport }: Pick<Props, 'cities' | 'sport'>) => {
  const [isShowAll, setIsShowAll] = React.useState(false);

  return (
    <div>
      <div className="grid grid-cols-1 gap-ds-2xl sm:grid-cols-2 lg:grid-cols-4">
        {cities.slice(0, VISIBLE_CITIES).map((city, index) => (
          <Link
            key={index}
            href={getCityCourtsPageUrlWithSport(city.slug, sport)}
            className="flex flex-col items-center justify-center rounded-lg p-ds-xl"
          >
            <div className="">
              <City className="w-12" />
            </div>
            <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {city.name}
            </h2>
            <span className="typography-product-body mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">{`${
              city.courtCount || 0
            } courts`}</span>
          </Link>
        ))}
      </div>
      <div
        className={classNames(
          'grid-cols-1 gap-ds-2xl sm:grid-cols-2 lg:grid-cols-4',
          isShowAll ? 'grid' : 'hidden',
        )}
      >
        {cities.slice(VISIBLE_CITIES).map((city, index) => (
          <Link
            key={index}
            href={getCityCourtsPageUrlWithSport(city.slug, sport)}
            className="flex flex-col items-center justify-center rounded-lg p-ds-xl"
          >
            <div className="">
              <City className="w-12" />
            </div>
            <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {city.name}
            </h2>
            <span className="typography-product-body mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">{`${
              city.courtCount || 0
            } courts`}</span>
          </Link>
        ))}
      </div>
      {cities.length > VISIBLE_CITIES && (
        <div className="mt-ds-2xl flex items-center justify-center">
          <Button
            type="button"
            variant="inverted"
            size="sm"
            className="px-6"
            onClick={() => setIsShowAll(true)}
          >
            Show all cities
          </Button>
        </div>
      )}
    </div>
  );
};

const Spacer = () => (
  <span className="block h-[6px] w-[6px] rounded-full bg-color-brand-primary">&nbsp;</span>
);

const CourtItem = ({
  title,
  cityName,
  slug,
  indoorCourtCount,
  outdoorCourtCount,
}: Venues['venues'][0] & { sport?: string }) => {
  return (
    <Link href={getCourtPageUrlWithSport(slug)}>
      <h3 className="flex items-center space-x-ds-md">
        <span className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {title}
        </span>
        <Spacer />
        <span className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {cityName}
        </span>
        <Spacer />
        <span className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {indoorCourtCount || 0 + outdoorCourtCount || 0} courts
        </span>
      </h3>
    </Link>
  );
};

const CourtList = ({
  venues,
  regionName,
  sport,
}: Venues & { regionName: string; sport?: string }) => {
  const [isShowAll, setIsShowAll] = React.useState(false);
  const sportName = sport ? capitalize(sport) : 'Pickleball';

  return (
    <div>
      <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        Every {sportName.toLowerCase()} court in {regionName}
      </h2>
      <div className="mt-ds-2xl grid grid-cols-1 gap-4 gap-x-2 lg:grid-cols-2">
        {venues.slice(0, VISIBLE_VENUES).map((item, index) => (
          <CourtItem key={index} {...item} sport={sportName} />
        ))}
      </div>
      <div
        className={classNames(
          'grid-cols-1 gap-4 gap-x-2 lg:grid-cols-2',
          isShowAll ? 'grid' : 'hidden',
        )}
      >
        {venues.slice(VISIBLE_VENUES).map((item, index) => (
          <CourtItem key={index} {...item} sport={sport} />
        ))}
      </div>
      <div className="mt-ds-2xl flex items-center justify-center">
        <Button
          type="button"
          variant="inverted"
          size="sm"
          className="px-6"
          onClick={() => setIsShowAll(true)}
        >
          Show all courts
        </Button>
      </div>
    </div>
  );
};

function formatDateRange(startDate: string, endDate: string): string {
  const yearOptions: Intl.DateTimeFormatOptions = { year: 'numeric' };
  const monthDayOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  const startMonthDay = new Intl.DateTimeFormat('en-US', monthDayOptions).format(
    new Date(startDate),
  );
  const endMonthDay = new Intl.DateTimeFormat('en-US', monthDayOptions).format(new Date(endDate));
  const year = new Intl.DateTimeFormat('en-US', yearOptions).format(new Date(endDate));

  return `${startMonthDay} - ${endMonthDay} ${year}`;
}

export default function RegionVenuesPage({
  countryId,
  countryName,
  countrySlug,
  regionName,
  regionSlug,
  cities,
  venues,
  events: tournaments,
  jsonLd,
  sport,
}: Props) {
  const {
    position,
    getEstimatedLocation,
    requestUserLocation,
    centerLatitude,
    centerLongitude,
    hasLocationPermission,
  } = useGeoLocation();

  const sportName = sport ? capitalize(sport) : 'Pickleball';
  const isTournamentSport = sportName === 'Pickleball';

  const decoratedTournaments = React.useMemo(() => {
    let processedTournaments = (tournaments || []).map((tournament) => {
      return {
        ...tournament,
        startTimestamp: new Date(tournament.startDateTime).getTime(),
        displayDate: formatDateRange(tournament.startDateTime, tournament.endDateTime),
        distance: Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: tournament.latitude,
              longitude: tournament.longitude,
            },
            coord2: {
              latitude: centerLatitude,
              longitude: centerLongitude,
            },
            unit: 'miles',
          }),
        ),
      };
    });

    return processedTournaments;
  }, [tournaments, centerLatitude, centerLongitude]);

  return (
    <>
      <Head
        canonicalType="currentUrlStripQuery"
        title={`${sportName} in ${regionName}`}
        description={`${sportName} courts, lessons, and tournaments by state. Play ${sportName.toLowerCase()} in ${regionName}`}
      />
      <SafeAreaPage isShowTopNav isHideSidebar>
        <main className="bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
          <div className="border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode">
            <div className="mx-auto flex max-w-6xl flex-col-reverse justify-between px-4 py-ds-lg md:flex-row md:items-center">
              <h1 className="typography-product-heading">{`${sportName} in ${regionName}`}</h1>
              <div className="mb-2 md:mb-0">
                <Breadcrumbs
                  breadcrumbs={
                    generateBreadcrumbs({
                      regionName,
                      regionSlug,
                      countryId,
                      countryName,
                      countrySlug,
                      sport: sport || '',
                    }).ui
                  }
                />
              </div>
            </div>
          </div>
          <div className="mx-auto max-w-6xl px-4 pt-6">
            <p className="typography-product-body mb-6 max-w-3xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {`Explore ${sport || 'pickleball'} across ${regionName} with Bounce. Browse courts, tournaments, and local play opportunities in cities throughout the state.`}
            </p>
            <CityLinkList cities={cities || []} sport={sport} />
          </div>
          {isTournamentSport && tournaments && !!tournaments.length && (
            <div className="px-4">
              <div className="mx-auto mt-10 max-w-6xl border-t border-color-border-input-lightmode pt-10 dark:border-color-border-input-darkmode">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Upcoming {sportName.toLowerCase()} tournaments in {regionName}
                </h2>
                <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2 xl:grid-cols-3">
                  {decoratedTournaments.slice(0, 6).map((tournament) => {
                    return (
                      <Link
                        key={tournament.id}
                        className="block w-full"
                        href={getEventUrl(tournament.id)}
                      >
                        <CardTournament
                          key={tournament.id}
                          listHoveredTournamentId={null}
                          setListHoveredTournamentId={() => {}}
                          tournamentId={tournament.id}
                          coverImageUrl={
                            tournament.coverImagePath
                              ? getImageUrl({
                                  url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                                  path: tournament.coverImagePath,
                                })
                              : ''
                          }
                          organizerImageUrl={
                            tournament.organizerImagePath
                              ? getImageUrl({
                                  url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                                  path: tournament.organizerImagePath,
                                })
                              : ''
                          }
                          title={tournament.title}
                          registrationClosedAt={tournament.registrationClosedAt}
                          startTimestamp={tournament.startTimestamp}
                          status={tournament.status}
                          sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                          sourceRegistrationCount={tournament.sourceRegistrationCount}
                          registrationCount={
                            tournament.registrationsAggregate?.aggregate?.count || 0
                          }
                          isExternal={tournament.isExternal}
                          displayDate={tournament.displayDate}
                          displayLocation={getEventLocation(tournament)}
                          distance={tournament.distance}
                          showImageAndPeople={false}
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="px-4 pb-20">
            <div className="mx-auto mt-10 max-w-6xl border-t border-color-border-input-lightmode pt-10 dark:border-color-border-input-darkmode">
              <CourtList regionName={regionName} venues={venues || []} sport={sport} />
            </div>
          </div>
        </main>
        <Footer />
      </SafeAreaPage>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
